/**
 * Admin Demo Requests Page
 * List and manage demo requests from the landing page
 */

import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { Trash2, RefreshCw, Mail, Building2, Briefcase, Users2, ExternalLink, FileDown } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { ExpandableText } from '../../components/ui/expandable-text';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import api from '../../services/api';

export const Route = createFileRoute('/admin/demo-requests')({
  component: AdminDemoRequests,
});

interface DemoRequest {
  id: string;
  fullName: string;
  workEmail: string;
  companyName: string;
  jobTitle: string | null;
  companyType: string | null;
  companySize: string | null;
  source: string | null;
  pageUrl: string | null;
  submittedAt: string | null;
  notes: string | null;
  resumeUrl: string | null;
  createdAt: string;
}

function AdminDemoRequests() {
  const [requests, setRequests] = useState<DemoRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState<string | null>(null);

  useEffect(() => {
    loadRequests();
  }, []);

  const loadRequests = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/demo-requests');
      setRequests(response.data);
    } catch (error) {
      console.error('Failed to load demo requests:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this demo request?')) return;
    setDeleting(id);
    try {
      await api.delete(`/admin/demo-requests/${id}`);
      setRequests((prev) => prev.filter((r) => r.id !== id));
    } catch (error) {
      console.error('Failed to delete demo request:', error);
    } finally {
      setDeleting(null);
    }
  };

  const openResume = async (resumeUrl: string) => {
    try {
      const response = await api.get(`/admin/careers/resume-url?path=${encodeURIComponent(resumeUrl)}`);
      window.open(response.data.url, '_blank');
    } catch {
      alert('Could not load resume — try again.');
    }
  };

  const formatCompanyType = (type: string | null) => {
    if (!type) return null;
    return type.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-4 border-primary border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Demo Requests & Applications</h1>
        <p className="text-muted-foreground">
          {requests.filter(r => r.source !== 'careers-page').length} demo request{requests.filter(r => r.source !== 'careers-page').length !== 1 ? 's' : ''} &nbsp;·&nbsp;{' '}
          {requests.filter(r => r.source === 'careers-page').length} internship application{requests.filter(r => r.source === 'careers-page').length !== 1 ? 's' : ''}
        </p>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5" />
              All Requests
            </CardTitle>
            <Button variant="outline" size="sm" onClick={loadRequests}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {requests.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">
              No demo requests yet
            </p>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Company / School</TableHead>
                    <TableHead>Details</TableHead>
                    <TableHead>Notes</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {requests.map((req) => (
                    <TableRow key={req.id}>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          {req.source === 'careers-page' && (
                            <Badge className="text-xs bg-accent text-accent-foreground border-primary/30">Intern</Badge>
                          )}
                          {req.fullName}
                        </div>
                      </TableCell>
                      <TableCell>
                        <a
                          href={`mailto:${req.workEmail}`}
                          className="text-primary hover:underline"
                        >
                          {req.workEmail}
                        </a>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Building2 className="h-4 w-4 text-muted-foreground" />
                          {req.companyName}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {req.jobTitle && (
                            <Badge variant="outline" className="text-xs">
                              <Briefcase className="h-3 w-3 mr-1" />
                              {req.jobTitle}
                            </Badge>
                          )}
                          {req.companyType && (
                            <Badge variant="secondary" className="text-xs">
                              {formatCompanyType(req.companyType)}
                            </Badge>
                          )}
                          {req.companySize && (
                            <Badge variant="secondary" className="text-xs">
                              <Users2 className="h-3 w-3 mr-1" />
                              {req.companySize}
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="max-w-[280px] align-top">
                        <ExpandableText text={req.notes} clampLines={3} />
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                        {new Date(req.createdAt).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          {req.resumeUrl && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => openResume(req.resumeUrl!)}
                              title="Download resume"
                            >
                              <FileDown className="h-4 w-4" />
                            </Button>
                          )}
                          {req.pageUrl && (
                            <Button
                              variant="ghost"
                              size="icon"
                              asChild
                              title="View source page"
                            >
                              <a href={req.pageUrl} target="_blank" rel="noopener noreferrer">
                                <ExternalLink className="h-4 w-4" />
                              </a>
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDelete(req.id)}
                            disabled={deleting === req.id}
                            className="text-destructive hover:text-destructive hover:bg-destructive/10"
                            title="Delete request"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
